import { useState, useRef } from "react";
import { useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Slider from "@mui/material/Slider";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { AUDIO_FORMATS } from "../../utils/constants";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import UploadFileButton from "../Buttons/UploadFileButton";
import {
  MUSIC_UPLOAD_TITLE,
  MUSIC_UPLOAD_BUTTON,
  MUSIC_UPLOAD_DESCRIPTION,
} from "../../i18n/keysTranslations";
import { getPathDatabaseByRole } from "../../actions/auth";
import Button from "../Buttons/Button";
import { getReactHookFormNestedError } from "../../utils/errors";

const MusicUploader = ({
  name = "musicUrl",
  disabled = false,
  errors = {},
  sx = {},
}) => {
  const { watch, setValue } = useFormContext();
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [isLoading, setIsLoading] = useState(false);

  const musicUrl = watch(name) || "";
  const hasMusic = Boolean(musicUrl);

  const error = getReactHookFormNestedError(errors, name);

  const pathStorage = `adsCampaigns/${userID}/`;

  // Handle audio loading
  const onLoadedData = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      audioRef.current.volume = volume;
    }
  };

  // Handle time update
  const onTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Handle play/pause
  const onTogglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Handle audio ended
  const onEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  // Handle seek
  const onSeek = (event, newValue) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  // Handle volume change
  const onVolumeChange = (event, newValue) => {
    setVolume(newValue);
    if (audioRef.current) {
      audioRef.current.volume = newValue;
    }
  };

  // Handle file upload
  const onFileUpload = async (urls) => {
    if (urls && urls.length > 0) {
      setValue(name, urls[0]);

      // Reset audio state
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    }
  };

  // Handle remove music
  const onRemoveMusic = () => {
    setValue(name, "");
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  };

  // Format time for display
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "0:00";

    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Get filename from URL
  const getFileName = (url) => {
    if (!url) return "";
    const parts = url.split("/");
    const filename = parts[parts.length - 1];
    return decodeURIComponent(filename).split("?")[0] || "music.mp3";
  };

  return (
    <Box sx={{ width: "100%", ...sx }} id={name} name={name}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        {t(MUSIC_UPLOAD_TITLE)}
      </Typography>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        {t(MUSIC_UPLOAD_DESCRIPTION)}
      </Typography>

      {/* Hidden upload button */}
      <UploadFileButton
        disableVerificationMedia={true}
        id={`music-upload-${name}`}
        value={musicUrl}
        onChange={onFileUpload}
        acceptedFormats={AUDIO_FORMATS}
        multiple={false}
        pathStorage={pathStorage}
        modeUpload="sync"
        onCallbackLoading={setIsLoading}
        isHidden={true}
      />

      {!hasMusic && (
        <Button
          color="secondary"
          fullWidth
          startIcon={<CloudUploadIcon />}
          onClick={() => {
            const input = document.getElementById(`music-upload-${name}`);
            if (input) {
              input.click();
            }
          }}
          loading={isLoading}
          disabled={disabled}
        >
          {t(MUSIC_UPLOAD_BUTTON)}
        </Button>
      )}

      {hasMusic && (
        <Card sx={{ bgcolor: "grey.50" }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            {/* Audio element */}
            <audio
              ref={audioRef}
              src={musicUrl}
              onLoadedData={onLoadedData}
              onTimeUpdate={onTimeUpdate}
              onEnded={onEnded}
              preload="metadata"
            />

            {/* Music info and controls */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <MusicNoteIcon color="primary" />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getFileName(musicUrl)}
              </Typography>
              <IconButton
                size="small"
                onClick={onRemoveMusic}
                disabled={disabled}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* Player controls */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IconButton
                onClick={onTogglePlayPause}
                disabled={disabled}
                color="primary"
              >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>

              <Typography variant="caption" sx={{ minWidth: 35 }}>
                {formatTime(currentTime)}
              </Typography>

              <Slider
                size="small"
                value={currentTime}
                max={duration}
                onChange={onSeek}
                disabled={disabled || !duration}
                sx={{ flex: 1, mx: 1 }}
              />

              <Typography variant="caption" sx={{ minWidth: 35 }}>
                {formatTime(duration)}
              </Typography>
            </Box>

            {/* Volume control */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <VolumeUpIcon fontSize="small" color="action" />
              <Slider
                size="small"
                value={volume}
                max={1}
                step={0.1}
                onChange={onVolumeChange}
                disabled={disabled}
                sx={{ width: 80 }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Display errors */}
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block" }}
        >
          {error.message}
        </Typography>
      )}
    </Box>
  );
};

export default MusicUploader;
